import { defineStore } from "pinia";
import type { Pagination } from "@/types";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { downloadFile } from "@coronet/cui";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { StoreModule } from "@/constants/store.ts";
import type { EndpointDgExclusionType } from "@/constants/endpoint-data-governance";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";

export interface EndpointDgExclusionItem {
  id: string;
  path: string;
  type: EndpointDgExclusionType;
  description?: string;
  timestamp: number;
  environmentVariableKey?: string;
}

export interface AddEndpointDgExclusionPayload {
  path: string;
  type: EndpointDgExclusionType;
  description?: string;
  environmentVariableKey?: string;
}

export interface EndpointDgExclusionsState {
  items: EndpointDgExclusionItem[];
  total: number;
  pagination: Pagination;
  loading: boolean;
}

const defaultState: EndpointDgExclusionsState = {
  items: [],
  total: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  loading: false,
};

export const useEndpointDgExclusionsStore = defineStore(
  StoreModule.ENDPOINT_DATA_GOVERNANCE_EXCLUSIONS,
  {
    state: (): EndpointDgExclusionsState => ({ ...defaultState }),
    actions: {
      setPagination(pagination: Pagination) {
        this.pagination = pagination;
      },
      resetPagination() {
        this.pagination = { ...defaultState.pagination };
      },
      async getItems() {
        this.loading = true;
        try {
          const filtersStore = useFiltersStore();
          const filters = filtersStore.filters[FilterContext.ENDPOINT_DATA_GOVERNANCE_EXCLUSIONS];
          const requestPayload = api.getEndpointDgExclusions({
            search: filters?.search,
            type: filters?.type,
            page: this.pagination.page,
            pageSize: this.pagination.pageSize,
          });
          const { data } = await axiosInstance.request(requestPayload);
          this.items = data.items;
          this.total = data.total;
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      },
      async addExclusion(payload: { item: AddEndpointDgExclusionPayload }) {
        const { path, type, description, environmentVariableKey } = payload.item;
        try {
          await axiosInstance.request(
            api.addEndpointDgExclusion({
              path,
              type,
              description,
              environmentVariableKey: environmentVariableKey || undefined,
            })
          );
          await this.getItems();
        } catch (error) {
          console.error(error);
        }
      },
      async removeExclusion(payload: { item: EndpointDgExclusionItem }) {
        try {
          await axiosInstance.request(api.deleteEndpointDgExclusion(payload.item.id));
          await this.getItems();
          useSnackbarStore().addGenericSuccess(
            i18n.global.t("snackbar.messages.addEndpointDgExclusion.successfullyRemoved")
          );
        } catch (error) {
          console.error(error);
        }
      },
      async importFromCsv(file: File) {
        const data = new FormData();
        data.append("file", file);
        return axiosInstance.request(api.uploadEndpointDgExclusionsCsv(data));
      },
      async downloadTemplate() {
        try {
          const { data, headers } = await axiosInstance.request(api.endpointDgExclusionsTemplate);
          const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
          downloadFile(blob, "EndpointDgExclusionListExample.csv");
        } catch (error) {
          console.error(error);
        }
      },
    },
  }
);
